import { z } from 'zod';
import { PdfStatusSchema } from './deposits.js';
const ISO_DATE = /^\d{4}-\d{2}-\d{2}$/;
// ─────────────────────── Credit card deposits (הפקדות אשראי) ─────────────────
//
// Card payments (one Arbox row per installment) are grouped by a "company" and
// deposited from the company's ledger account (כרטסת) to a bank's ledger
// account. The company is the card issuer or the acquirer — the box picks the
// mode per location (migration 014).
/** 'issuer' = חברה מנפיקה (from Arbox). 'acquirer' = חברה סולקת (box-defined). */
export const CreditGroupingSchema = z.enum(['issuer', 'acquirer']);
// ───────────────────────────────── Settings ─────────────────────────────────
export const CreditBankSchema = z.object({
    id: z.number(),
    name: z.string(),
    ledger_account: z.string(),
});
export const CreditAcquirerSchema = z.object({
    id: z.number(),
    name: z.string(),
    ledger_account: z.string().nullable(),
    default_bank_id: z.number().nullable(),
});
export const CreditIssuerSchema = z.object({
    /** Normalized issuer name, e.g. "ויזה". The key. */
    name: z.string(),
    /** Issuer mode only. */
    ledger_account: z.string().nullable(),
    default_bank_id: z.number().nullable(),
    /** Acquirer mode only. */
    acquirer_id: z.number().nullable(),
});
export const CreditSettingsSchema = z.object({
    grouping: CreditGroupingSchema,
    banks: z.array(CreditBankSchema),
    acquirers: z.array(CreditAcquirerSchema),
    issuers: z.array(CreditIssuerSchema),
});
/** Settings requests carry the header location; the server resolves the scope
 *  (location 0 when the box doesn't manage locations). */
const ScopeSchema = z.object({ location_id: z.number() });
export const CreditSettingsQuerySchema = z.object({
    location_id: z.coerce.number(),
});
export const UpdateCreditGroupingRequestSchema = ScopeSchema.extend({
    grouping: CreditGroupingSchema,
});
const LedgerAccountSchema = z.string().trim().min(1).max(40);
export const UpsertCreditBankRequestSchema = ScopeSchema.extend({
    name: z.string().trim().min(1).max(120),
    ledger_account: LedgerAccountSchema,
});
export const UpsertCreditAcquirerRequestSchema = ScopeSchema.extend({
    name: z.string().trim().min(1).max(120),
    ledger_account: LedgerAccountSchema.nullable(),
    default_bank_id: z.number().nullable(),
});
/** Partial update of one issuer row. Omitted fields are kept. */
export const UpdateCreditIssuerRequestSchema = ScopeSchema.extend({
    name: z.string().min(1),
    ledger_account: LedgerAccountSchema.nullable().optional(),
    default_bank_id: z.number().nullable().optional(),
    acquirer_id: z.number().nullable().optional(),
});
// ──────────────────────────── Arbox receipt cards ───────────────────────────
/** A receipt card (כרטסת) of the location's accounting entity in Arbox
 *  (POST /accountCards). Ledger accounts in the settings are picked from these. */
export const CreditAccountCardSchema = z.object({
    id: z.number(),
    name: z.string(),
    account_number: z.string(),
});
export const CreateCreditAccountCardRequestSchema = ScopeSchema.extend({
    name: z.string().trim().min(1).max(120),
    account_number: z.string().trim().regex(/^\d{1,12}$/),
});
// ───────────────────────────── Pending payments ─────────────────────────────
/** One card installment from Arbox, with the company it resolves to. */
export const CreditPaymentSchema = z.object({
    /** `<receipt_type>_<receipt_number>_<last4>_<installment>_<settlement_date>`. */
    external_receipt_id: z.string(),
    receipt_number: z.string(),
    receipt_url: z.string().url().nullable(),
    customer_external_id: z.string().nullable(),
    customer_name: z.string(),
    /** Normalized issuer, e.g. "ויזה"; "אשראי (ללא מותג)" for old rows without a brand. */
    issuer: z.string(),
    card_last4: z.string().nullable(),
    /** Arbox's check_number on a card row = the voucher / approval number. */
    voucher_number: z.string().nullable(),
    /** 1-based; null when Arbox doesn't say. */
    installment_number: z.number().nullable(),
    /** Rows of the same receipt + card in the fetch; null when unknown. */
    installments_total: z.number().nullable(),
    /** ILS. Negative on a refund. */
    amount: z.number(),
    /** Transaction date, 'YYYY-MM-DD'. */
    issue_date: z.string(),
    /** Expected settlement date (Arbox due_date), 'YYYY-MM-DD'. */
    settlement_date: z.string().nullable(),
    project_id: z.number(),
    /** Company key in the current mode: issuer name, or `a<acquirer_id>`.
     *  null = not configured yet (acquirer mode: issuer not mapped). */
    company_key: z.string().nullable(),
    company_name: z.string().nullable(),
});
export const CreditDateFieldSchema = z.enum(['settlement_date', 'issue_date']);
export const ListCreditPendingQuerySchema = z.object({
    dateField: CreditDateFieldSchema,
    from: z.string().regex(ISO_DATE),
    to: z.string().regex(ISO_DATE),
    location_id: z.coerce.number(),
    project_id: z.coerce.number().optional(),
});
// ──────────────────────────────── Deposits ──────────────────────────────────
export const CreateCreditDepositRequestSchema = z.object({
    location_id: z.number(),
    project_id: z.number().optional(),
    deposit_date: z.string().regex(ISO_DATE),
    bank_id: z.number(),
    notes: z.string().max(1000).nullable().optional(),
    external_receipt_ids: z.array(z.string()).min(1),
});
export const CreditJournalStatusSchema = z.enum([
    'skipped',
    'pending',
    'synced',
    'failed',
    'cancel_pending',
    'cancelled',
    'cancel_failed',
]);
export const CreditDepositSummarySchema = z.object({
    id: z.number(),
    location_id: z.number(),
    project_id: z.number(),
    deposit_number: z.number(),
    deposit_date: z.string(),
    created_at: z.string(),
    grouping: CreditGroupingSchema,
    company_name: z.string(),
    company_ledger_account: z.string(),
    bank_name: z.string(),
    bank_ledger_account: z.string(),
    total: z.number(),
    payment_count: z.number(),
    pdf_status: PdfStatusSchema,
    journal_status: CreditJournalStatusSchema,
    journal_last_error: z.string().nullable(),
    journal_reference: z.string(),
    cancelled_at: z.string().nullable(),
    notes: z.string().nullable(),
    created_by_name: z.string().nullable(),
});
export const CreditDepositReceiptSchema = z.object({
    id: z.number(),
    external_receipt_id: z.string(),
    receipt_number: z.string(),
    customer_name: z.string(),
    issuer: z.string(),
    card_last4: z.string().nullable(),
    voucher_number: z.string().nullable(),
    installment_number: z.number().nullable(),
    installments_total: z.number().nullable(),
    amount: z.number(),
    issue_date: z.string(),
    settlement_date: z.string().nullable(),
});
export const ListCreditDepositsQuerySchema = z.object({
    location_id: z.coerce.number(),
    project_id: z.coerce.number().optional(),
    from: z.string().regex(ISO_DATE),
    to: z.string().regex(ISO_DATE),
});
export const CancelCreditDepositRequestSchema = z.object({
    reason: z.string().max(500).optional(),
});
//# sourceMappingURL=credit.js.map